import { ArrowCounterClockwiseIcon, PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { DeactivateMachineTypeConfirmDialog } from '@/components/machine-types/DeactivateMachineTypeConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import {
  deactivateMachineTypeRequest,
  getMachineTypeRequest,
  isDeactivateMachineTypeBlocked,
  reactivateMachineTypeRequest,
} from '@/services/machine-types';
import type { MachineTypeDetail } from '@/types/machine-types';

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function MachineTypeDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [deactivateOpen, setDeactivateOpen] = useState<boolean>(false);
  const [blocked, setBlocked] = useState<{ readonly affectedMachineCount: number } | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const detailQuery = useQuery<MachineTypeDetail, ApiError>({
    queryKey: ['machine-type', id],
    queryFn: () => getMachineTypeRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5 * 60_000,
  });

  const deactivateMutation = useMutation<undefined, ApiError, { force: boolean }>({
    mutationFn: async ({ force }) => {
      await deactivateMachineTypeRequest(id, force);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Machine type deactivated');
      void queryClient.invalidateQueries({ queryKey: ['machine-types'] });
      void queryClient.invalidateQueries({ queryKey: ['machine-type', id] });
      setDeactivateOpen(false);
      setBlocked(null);
      setDeactivateError(null);
      void navigate('/machine-types');
    },
    onError: (err) => {
      if (isDeactivateMachineTypeBlocked(err)) {
        setBlocked({ affectedMachineCount: err.affectedMachineCount });
        setDeactivateError(null);
      } else {
        setDeactivateError(err.detail ?? err.message);
      }
    },
  });

  const reactivateMutation = useMutation<undefined, ApiError>({
    mutationFn: async () => {
      await reactivateMachineTypeRequest(id);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Machine type reactivated');
      void queryClient.invalidateQueries({ queryKey: ['machine-types'] });
      void queryClient.invalidateQueries({ queryKey: ['machine-type', id] });
    },
    onError: (err) => {
      toast.error('Could not reactivate', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('machineTypes.read')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/machine-types" replace />;
  }

  const canEdit = permissions.includes('machineTypes.update');
  const canDeactivate = permissions.includes('machineTypes.delete');
  const mt = detailQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{mt?.name ?? 'Machine type'}</h1>
          <p className="text-muted-foreground">
            {mt?.description ?? 'Schema viewer and lifecycle controls.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {mt && (
            <Badge variant={mt.isActive ? 'default' : 'secondary'} data-testid="mt-detail-status">
              {mt.isActive ? 'Active' : 'Inactive'}
            </Badge>
          )}
          {mt !== undefined && canEdit && mt.isActive && (
            <Button asChild data-testid="mt-detail-edit">
              <Link to={`/machine-types/${String(id)}/edit`}>
                <PencilSimpleIcon size={16} weight="bold" /> Edit
              </Link>
            </Button>
          )}
          {mt !== undefined && canDeactivate && mt.isActive && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setBlocked(null);
                setDeactivateError(null);
                setDeactivateOpen(true);
              }}
              data-testid="mt-detail-deactivate"
            >
              <TrashIcon size={16} weight="bold" /> Deactivate
            </Button>
          )}
          {mt !== undefined && canEdit && !mt.isActive && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reactivateMutation.mutate();
              }}
              disabled={reactivateMutation.isPending}
              data-testid="mt-detail-reactivate"
            >
              <ArrowCounterClockwiseIcon size={16} weight="bold" />{' '}
              {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
            </Button>
          )}
        </div>
      </div>

      {detailQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="mt-detail-loading">
          Loading machine type…
        </p>
      )}
      {detailQuery.isError && (
        <p className="text-sm text-destructive" data-testid="mt-detail-error">
          Failed to load machine type.
        </p>
      )}

      {mt && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Schema</CardTitle>
              <CardDescription>
                Read-only view of the custom-field schema. Use Edit to modify.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mt.customFields.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="mt-detail-schema-empty">
                  No custom fields defined.
                </p>
              ) : (
                <div className="overflow-x-auto">
                <Table data-testid="mt-detail-schema-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead>Data type</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead>Dropdown options</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mt.customFields.map((field) => (
                      <TableRow key={field.key} data-testid={`mt-detail-schema-row-${field.key}`}>
                        <TableCell>{field.label}</TableCell>
                        <TableCell className="font-mono text-xs">{field.key}</TableCell>
                        <TableCell>{field.dataType}</TableCell>
                        <TableCell>
                          {field.required ? (
                            <Badge variant="default">Required</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {field.dropdownOptions !== null && field.dropdownOptions.length > 0
                            ? field.dropdownOptions.join(', ')
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div>
                <div className="text-muted-foreground text-xs">Created</div>
                <div className="font-mono text-xs">{formatDateTime(mt.createdAt)}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Updated</div>
                <div className="font-mono text-xs">{formatDateTime(mt.updatedAt)}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Machines using this type</div>
                <div data-testid="mt-detail-machines-count">{String(mt.machinesCount)}</div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {mt && (
        <DeactivateMachineTypeConfirmDialog
          open={deactivateOpen}
          onOpenChange={(open) => {
            setDeactivateOpen(open);
            if (!open) {
              setBlocked(null);
              setDeactivateError(null);
              deactivateMutation.reset();
            }
          }}
          typeName={mt.name}
          blocked={blocked}
          submitting={deactivateMutation.isPending}
          errorMessage={deactivateError}
          onConfirm={(force) => {
            setDeactivateError(null);
            deactivateMutation.mutate({ force });
          }}
          onCancel={() => {
            setDeactivateOpen(false);
            setBlocked(null);
            setDeactivateError(null);
          }}
        />
      )}
    </div>
  );
}
